import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import { useCart } from '@/hooks/useCart';
import CheckoutSteps from './components/CheckoutSteps';
import DeliveryForm, { type DeliveryData } from './components/DeliveryForm';
import PaymentForm, { type PaymentData } from './components/PaymentForm';
import OrderSummary from './components/OrderSummary';
import SeoHead from '@/components/feature/SeoHead';

const EMPTY_DELIVERY: DeliveryData = {
  method: 'courier', firstName: '', lastName: '', phone: '', email: '',
  city: '', address: '', apartment: '', comment: '', pickupPoint: '',
};

const EMPTY_PAYMENT: PaymentData = {
  method: 'card', cardNumber: '', cardName: '', cardExpiry: '', cardCvv: '',
  promoCode: '', promoApplied: false, useBonus: false, bonusAmount: 0,
};

const CheckoutPage = () => {
  const [step, setStep] = useState(1);
  const [delivery, setDelivery] = useState<DeliveryData>(EMPTY_DELIVERY);
  const [payment, setPayment] = useState<PaymentData>(EMPTY_PAYMENT);
  const { items, totalPrice, clearCart } = useCart();
  const navigate = useNavigate();

  useEffect(() => { window.scrollTo(0, 0); }, [step]);

  const handleConfirm = () => {
    clearCart();
    navigate('/checkout/success');
  };

  if (items.length === 0) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center" style={{ background: '#05050a' }}>
        <Navbar />
        <div className="text-center pt-32 pb-20">
          <div className="w-20 h-20 flex items-center justify-center rounded-2xl mx-auto mb-6" style={{ background: 'rgba(255,45,120,0.08)', border: '1px solid rgba(255,45,120,0.15)' }}>
            <i className="ri-shopping-bag-line text-3xl" style={{ color: 'rgba(255,45,120,0.4)' }} />
          </div>
          <h2 className="font-orbitron font-bold text-2xl text-white mb-3">Корзина пуста</h2>
          <p className="font-mono text-sm mb-8" style={{ color: 'rgba(255,255,255,0.35)' }}>Добавьте товары, чтобы оформить заказ</p>
          <Link to="/catalog" className="whitespace-nowrap inline-flex items-center gap-2 px-8 py-3 rounded-full font-orbitron font-bold text-sm cursor-pointer" style={{ background: '#ff2d78', color: '#fff', boxShadow: '0 0 15px rgba(255,45,120,0.4)' }}>
            <i className="ri-medicine-bottle-line" />
            В каталог
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#05050a' }}>
      <SeoHead
        title="Оформление заказа — NeonPharma"
        description="Оформление заказа в интернет-аптеке NeonPharma."
        noIndex={true}
      />
      <Navbar />

      <div className="pt-28 pb-20 px-6 lg:px-12">
        <div className="max-w-6xl mx-auto">

          {/* Header */}
          <div className="mb-8">
            <nav className="flex items-center gap-2 font-mono text-xs mb-4" style={{ color: 'rgba(255,255,255,0.3)' }}>
              <Link to="/" className="hover:text-white transition-colors">Главная</Link>
              <i className="ri-arrow-right-s-line" />
              <span style={{ color: '#ff2d78' }}>Оформление заказа</span>
            </nav>
            <h1 className="font-orbitron font-black text-3xl md:text-4xl text-white">
              ОФОРМЛЕНИЕ <span style={{ color: '#ff2d78' }}>ЗАКАЗА</span>
            </h1>
          </div>

          {/* Steps */}
          <CheckoutSteps current={step} />

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main form */}
            <div className="lg:col-span-2">
              <div className="rounded-2xl p-6 md:p-8" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)' }}>
                {step === 1 && (
                  <DeliveryForm data={delivery} onChange={setDelivery} onNext={() => setStep(2)} />
                )}
                {step === 2 && (
                  <PaymentForm
                    data={payment}
                    onChange={setPayment}
                    onNext={() => setStep(3)}
                    onBack={() => setStep(1)}
                    totalPrice={totalPrice}
                  />
                )}
                {step === 3 && (
                  <div className="space-y-6">
                    <h3 className="font-orbitron font-bold text-base text-white">Проверьте данные заказа</h3>
                    <OrderSummary delivery={delivery} payment={payment} />
                    <div className="flex gap-3">
                      <button
                        onClick={() => setStep(2)}
                        className="whitespace-nowrap flex items-center justify-center gap-2 px-6 py-4 rounded-2xl font-mono text-sm cursor-pointer transition-all duration-200"
                        style={{ border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.5)' }}
                        onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.25)'; (e.currentTarget as HTMLElement).style.color = '#fff'; }}
                        onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.1)'; (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.5)'; }}
                      >
                        <i className="ri-arrow-left-line" />
                        Назад
                      </button>
                      <button
                        onClick={handleConfirm}
                        className="whitespace-nowrap flex-1 flex items-center justify-center gap-2 py-4 rounded-2xl font-orbitron font-bold text-sm cursor-pointer transition-all duration-300"
                        style={{ background: '#ff2d78', color: '#fff', boxShadow: '0 0 20px rgba(255,45,120,0.4)' }}
                        onMouseEnter={e => { (e.currentTarget as HTMLElement).style.boxShadow = '0 0 30px rgba(255,45,120,0.7)'; (e.currentTarget as HTMLElement).style.transform = 'translateY(-1px)'; }}
                        onMouseLeave={e => { (e.currentTarget as HTMLElement).style.boxShadow = '0 0 20px rgba(255,45,120,0.4)'; (e.currentTarget as HTMLElement).style.transform = 'translateY(0)'; }}
                      >
                        <i className="ri-secure-payment-line" />
                        ОПЛАТИТЬ {totalPrice.toLocaleString()} ₽
                      </button>
                    </div>
                    <p className="font-mono text-xs text-center" style={{ color: 'rgba(255,255,255,0.25)' }}>
                      Нажимая «Оплатить», вы соглашаетесь с условиями оферты и политикой конфиденциальности
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Sidebar — cart summary */}
            <div className="space-y-4">
              {/* Mini cart */}
              <div className="rounded-2xl overflow-hidden" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)' }}>
                <div className="px-5 py-4 flex items-center justify-between" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                  <p className="font-orbitron font-bold text-sm text-white">Ваш заказ</p>
                  <span className="font-mono text-xs px-2 py-0.5 rounded-full" style={{ background: 'rgba(255,45,120,0.1)', color: '#ff2d78', border: '1px solid rgba(255,45,120,0.2)' }}>
                    {items.length} тов.
                  </span>
                </div>
                <div className="p-4 space-y-3 max-h-64 overflow-y-auto" style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(255,45,120,0.2) transparent' }}>
                  {items.map(item => (
                    <div key={item.id} className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${item.color}10`, border: `1px solid ${item.color}20` }}>
                        <img src={item.image} alt={item.name} className="w-8 h-8 object-contain" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-mono text-xs text-white line-clamp-1">{item.name}</p>
                        <p className="font-mono text-[10px]" style={{ color: 'rgba(255,255,255,0.3)' }}>× {item.quantity}</p>
                      </div>
                      <p className="font-orbitron font-bold text-xs shrink-0" style={{ color: item.color }}>
                        {(item.price * item.quantity).toLocaleString()} ₽
                      </p>
                    </div>
                  ))}
                </div>
                <div className="px-5 py-4 flex items-center justify-between" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                  <span className="font-mono text-sm" style={{ color: 'rgba(255,255,255,0.5)' }}>Итого</span>
                  <span className="font-orbitron font-black text-lg" style={{ color: '#ff2d78' }}>{totalPrice.toLocaleString()} ₽</span>
                </div>
              </div>

              {/* Trust badges */}
              <div className="p-4 rounded-2xl space-y-3" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
                {[
                  { icon: 'ri-shield-check-line', color: '#39ff14', title: 'Безопасная оплата', desc: 'SSL-шифрование данных' },
                  { icon: 'ri-truck-line', color: '#00f5ff', title: 'Быстрая доставка', desc: 'Курьером за 2–4 часа' },
                  { icon: 'ri-refund-2-line', color: '#f0ff00', title: 'Возврат 14 дней', desc: 'Без вопросов' },
                  { icon: 'ri-customer-service-2-line', color: '#ff2d78', title: 'Поддержка 24/7', desc: '8 800 123-45-67' },
                ].map(b => (
                  <div key={b.title} className="flex items-center gap-3">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg shrink-0" style={{ background: `${b.color}10` }}>
                      <i className={`${b.icon} text-sm`} style={{ color: b.color }} />
                    </div>
                    <div>
                      <p className="font-mono text-xs text-white">{b.title}</p>
                      <p className="font-mono text-[10px]" style={{ color: 'rgba(255,255,255,0.3)' }}>{b.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CheckoutPage;
